/**
 * Canonical "Xh Ym" duration formatting.
 *
 * DIVERGENCE NOTE — consolidated from three previously independent copies
 * whose rounding of leftover minutes differed:
 *   - packages/shared WorkSessionEditor.types.ts: truncated (`minutes % 60`)
 *   - packages/shared DayTimeline.types.ts:       Math.round(minutes % 60)
 *   - apps/mobile TimeClockReportGrid.tsx:        Math.round(minutes % 60),
 *     and always renders both units ("0h 30m", "2h 0m")
 * The `round` / `showZeroUnits` options preserve each call site's historical
 * output; unifying them on a single mode should be a deliberate future
 * change, not a side effect of this consolidation. (Known quirk preserved:
 * with `round: 'round'`, e.g. 119.6 minutes renders "1h 60m".)
 */
export interface FormatDurationOptions {
  /** How to handle fractional leftover minutes. Default: 'floor' (truncate). */
  round?: 'floor' | 'round';
  /** Always render both units ("0h 30m", "2h 0m") instead of omitting zero units. Default: false. */
  showZeroUnits?: boolean;
}

/**
 * Format a duration in minutes as hours and minutes
 * ("45m", "2h", "2h 30m" — or "0h 45m" / "2h 0m" with `showZeroUnits`).
 */
export function formatDuration(minutes: number, opts?: FormatDurationOptions): string {
  const hours = Math.floor(minutes / 60);
  const mins = opts?.round === 'round' ? Math.round(minutes % 60) : Math.floor(minutes % 60);
  if (opts?.showZeroUnits) return `${hours}h ${mins}m`;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
